/**
 * Core-API search for positions the lightweight classic model cannot
 * evaluate: games with tile gimmicks (ice/swamp) or unspent character
 * abilities (ninja wallJump, mage removeWall, explorer diagonalStep).
 *
 * Correctness first: every candidate — including {type:'ability'} moves —
 * is evaluated by actually applying it with core applyMove and scoring the
 * resulting state with core shortestPathLength, which already folds in
 * ninja jump budgets, ice slides and swamp semantics (contract §8-8).
 * This is slower than the model (state clones + character-aware BFS) but
 * still far inside the browser budget on a 9x9 board.
 *
 * Search shape mirrors the classic normal AI: 2 plies — score all my legal
 * moves, then refine the top K with the opponent's best immediate reply.
 * normal and hard share the code and differ in K (and hard additionally
 * refines with a wider beam).
 */

import { legalMoves, applyMove, shortestPathLength } from '../core/index.js';

const EPS = 1e-9;

function same(a, b) {
  return a === b || Math.abs(a - b) <= EPS;
}

/**
 * Static evaluation of a full core state from `me`'s perspective.
 * shortestPathLength is character/gimmick-aware. A legally reached state
 * always has paths for both players, so -1 never happens here; guard with
 * +/-Infinity anyway. wallWeight lets each difficulty level value
 * walls-in-hand differently, mirroring the classic model's per-level
 * weight (src/ai/classic.js HARD_DEFAULTS).
 */
function coreEval(state, me, wallWeight) {
  const opp = 1 - me;
  const dMe = shortestPathLength(state, me);
  if (dMe < 0) return -Infinity;
  const dOpp = shortestPathLength(state, opp);
  if (dOpp < 0) return Infinity;
  return (
    dOpp -
    dMe +
    wallWeight * (state.players[me].wallsLeft - state.players[opp].wallsLeft)
  );
}

/**
 * Swamp-aware tempo correction: if this state's player-to-move is swamped,
 * they lose a movement turn, which the raw distance difference does not
 * see. Nudge the eval so an AI values pushing the opponent into a swamp
 * (and avoids parking in one) without a deeper search.
 */
function swampBonus(state, me) {
  let bonus = 0;
  if (state.players[1 - me].effects && state.players[1 - me].effects.swamped) {
    bonus += 1;
  }
  if (state.players[me].effects && state.players[me].effects.swamped) {
    bonus -= 1;
  }
  return bonus;
}

/**
 * Opponent's best immediate reply value (from THEIR perspective) after
 * `next`. Full legal move list — abilities included. Returns Infinity if
 * the opponent can win on the spot.
 */
function bestReplyEval(next, wallWeight) {
  const opp = next.current;
  let best = -Infinity;
  for (const mv of legalMoves(next)) {
    const after = applyMove(next, mv);
    if (after.winner === opp) return Infinity;
    const s = coreEval(after, opp, wallWeight) + swampBonus(after, opp);
    if (s > best) best = s;
  }
  return best;
}

/**
 * 2-ply search over the full core move list. opts: { topK, wallWeight }.
 */
export function chooseGeneric(state, rng, opts) {
  const { topK, wallWeight } = opts;
  const me = state.current;
  const moves = legalMoves(state);
  if (moves.length === 1) return moves[0]; // forced (e.g. pass)

  // --- ply-1: apply + score every legal move ------------------------
  const scored = [];
  const wins = [];
  for (const mv of moves) {
    if (mv.type === 'pass') continue; // never voluntary
    const next = applyMove(state, mv);
    if (next.winner === me) {
      wins.push(mv);
      continue;
    }
    scored.push({
      mv,
      next,
      s1: coreEval(next, me, wallWeight) + swampBonus(next, me),
    });
  }

  if (wins.length > 0) return wins[Math.floor(rng() * wins.length)];
  if (scored.length === 0) return moves[Math.floor(rng() * moves.length)];

  // --- ply-2 refinement on the top K --------------------------------
  scored.sort((a, b) => b.s1 - a.s1); // stable sort -> deterministic
  const top = scored.slice(0, topK);
  for (const item of top) {
    const reply = bestReplyEval(item.next, wallWeight);
    item.s2 = reply === Infinity ? -Infinity : -reply;
  }

  let best = top[0];
  for (const item of top) {
    if (
      (!same(item.s2, best.s2) && item.s2 > best.s2) ||
      (same(item.s2, best.s2) && !same(item.s1, best.s1) && item.s1 > best.s1)
    ) {
      best = item;
    }
  }
  const tied = top.filter(
    (item) => same(item.s2, best.s2) && same(item.s1, best.s1)
  );
  return tied[Math.floor(rng() * tied.length)].mv;
}
